import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { config } from '../../../config';
import { logError } from '../../../utils/logger';
import { SectionTitle } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import { uploadFile } from '../../shared/service';

interface UploadedRow {
  id: string | number;
  name: string;
  url: string;
  fileType: string;
  uploadedAt: string;
}

/** Direct link to the inline-serving endpoint, `GET /api/file/{id}`. */
const fileUrl = (id: string | number) => `${config.env.API_URL ?? ''}/api/file/${id}`;

/**
 * File management. The backend keeps no listing endpoint — `FileController` only
 * exposes upload and fetch-by-id — so this screen covers uploading, capturing the
 * returned ids (which other modules reference as `file_id`), and opening any file
 * by id.
 */
export default function FilesPage() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploads, setUploads] = useState<UploadedRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [lookupId, setLookupId] = useState('');

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        try {
          const result = await uploadFile(file);
          if (result.id == null) throw new Error('Upload succeeded but no file id was returned');

          setUploads((prev) => [
            {
              id: result.id!,
              name: result.name ?? file.name,
              url: result.url ?? '',
              fileType: result.fileType ?? file.type,
              uploadedAt: new Date().toLocaleString(),
            },
            ...prev,
          ]);
          toast.success(`${file.name} uploaded`);
        } catch (err) {
          toast.error(
            logError({
              message: `Failed to upload ${file.name}`,
              error: err,
              code: 'FILE_UPLOAD_FAILED',
            })
          );
        }
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const columns = [
    { id: 'id', dataKey: 'id', label: 'File ID', align: 'center' as const, sortable: true },
    { id: 'name', dataKey: 'name', label: 'Name', align: 'left' as const, sortable: true },
    { id: 'fileType', dataKey: 'fileType', label: 'Type', align: 'left' as const },
    { id: 'uploadedAt', dataKey: 'uploadedAt', label: 'Uploaded', align: 'center' as const },
    {
      id: 'actions',
      dataKey: 'id',
      label: 'Actions',
      align: 'center' as const,
      renderCell: (id: string | number) => (
        <Stack direction='row' gap={1} justifyContent='center'>
          <Tooltip title='Open file'>
            <IconButton
              size='small'
              component='a'
              href={fileUrl(id)}
              target='_blank'
              rel='noreferrer'
              aria-label='Open file'
            >
              <Icon icon='mdi:open-in-new' width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Copy file ID'>
            <IconButton
              size='small'
              onClick={() => {
                navigator.clipboard?.writeText(String(id));
                toast.success(`Copied file ID ${id}`);
              }}
              aria-label='Copy file ID'
            >
              <Icon icon='mdi:content-copy' width={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const openById = () => {
    const id = lookupId.trim();
    if (!id) return;
    window.open(fileUrl(id), '_blank', 'noreferrer');
  };

  return (
    <Stack gap={3}>
      <SectionTitle
        title='FILE MANAGEMENT'
        action={
          <Button
            variant='contained'
            startIcon={<Icon icon='mdi:upload' />}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Upload Files'}
          </Button>
        }
      />

      <input
        ref={inputRef}
        type='file'
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      <Box
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        sx={{
          border: '2px dashed',
          borderColor: 'grey.400',
          borderRadius: 2,
          padding: 4,
          textAlign: 'center',
          cursor: 'pointer',
        }}
        onClick={() => inputRef.current?.click()}
      >
        <Icon icon='mdi:cloud-upload-outline' width={40} />
        <Typography sx={{ marginTop: 1 }}>
          Drop files here, or click to choose. Images, video and documents are accepted.
        </Typography>
      </Box>

      {uploading && <LinearProgress />}

      <Alert severity='info'>
        Uploads are stored centrally and referenced elsewhere by their numeric file ID (used as
        <code> file_id</code>, <code>photo</code>, <code>contract</code> and
        <code> payment_proof</code> in other forms). The API has no list-all route, so this table
        shows files uploaded during this session.
      </Alert>

      <Table
        columns={columns}
        data={uploads}
        loading={false}
        emptyMessage='No files uploaded in this session'
      />

      <Stack direction='row' gap={2} alignItems='flex-start' sx={{ maxWidth: 460 }}>
        <TextField
          label='Open file by ID'
          type='number'
          size='small'
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
          fullWidth
        />
        <Button variant='outlined' onClick={openById} disabled={!lookupId.trim()} sx={{ height: 40 }}>
          Open
        </Button>
      </Stack>
    </Stack>
  );
}
